import React, {useEffect, useMemo, useState} from 'react';
import {Box, Container, Grid, InputAdornment, MenuItem, Select, TextField, Tooltip} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DynamicIcon from './DynamicIcon';
import Typography from '@mui/material/Typography';
import {useNavigate, useParams} from 'react-router-dom';
import {DriverCard} from './DriverCard';
import {connect} from 'react-redux';
import {getDrivers} from '../store/actions/apiCalls';
import IconButton from '@mui/material/IconButton';
import {HashLink} from 'react-router-hash-link';

const PortfolioBrowser = ({drivers, getDrivers, categories}) => {
	const {platform = 'all', category = 'all', driverId} = useParams();
	const navigate = useNavigate();
	const [search, setSearch] = useState('');

	useEffect(() => {
		getDrivers('/api/v1/drivers', {platform, category, driverId});
	}, [platform, category, driverId, getDrivers]);

	const visibleDrivers = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return drivers;
		return drivers.filter(d => `${d.title} ${d.text}`.toLowerCase().includes(needle));
	}, [drivers, search]);

	return (
		<Container maxWidth="lg" sx={{paddingBottom: 6}}>
			<div id={'portfolioBrowser'}/>
			<div id={'mainPanel'}/>
			<Grid container spacing={3}>
				<Grid item xs={12} paddingTop={'10px'}>
					<Box sx={{
						borderRadius: 5,
						backgroundColor: 'primary.main',
						display: 'flex',
						alignItems: 'center',
						flexWrap: {xs: 'wrap', md: 'nowrap'},
						gap: 1,
						paddingLeft: 2,
						paddingRight: 2,
						paddingTop: 1,
						paddingBottom: 1,
					}}>
						<Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
							<DynamicIcon name={category} style={{fontSize: '37px'}}/>
							<Typography color={'primary.contrastText'} variant={'h6'} textTransform={'uppercase'}
							            sx={{display: {xs: 'none', sm: 'block'}, paddingLeft: 1, whiteSpace: 'nowrap'}}>
								{category === 'all' ? 'All Categories' : category}
							</Typography>
						</Box>
						<Box sx={{display: {xs: 'flex', sm: 'none'}, flexGrow: 1}}>
							<Select
								id="category-select"
								value={category}
								onChange={(e) => navigate(`/portfolio/${platform}/${e.target.value}#portfolioBrowser`)}
								variant="standard"
								size={'small'}
								disableUnderline
								sx={{width: '100%', color: 'primary.contrastText'}}
							>
								<MenuItem value={'all'}>All</MenuItem>
								{categories.map((cat) => (
									<MenuItem key={cat.value} value={cat.value}>{cat.name}</MenuItem>
								))}
							</Select>
						</Box>
						<Box sx={{display: {xs: 'none', sm: 'flex'}, flexShrink: 0}}>
							{categories.filter((cat) => (cat.value !== category)).map((cat) => (
								<Tooltip title={cat.name} key={cat.value}>
									<IconButton component={HashLink} smooth
									            to={`/portfolio/${platform}/${cat.value}#portfolioBrowser`}>
										<DynamicIcon name={cat.value} style={{fontSize: '33px'}}/>
									</IconButton>
								</Tooltip>
							))}
						</Box>
						<Box sx={{flexGrow: 1, display: {xs: 'none', md: 'block'}}}/>
						<TextField
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search drivers…"
							size="small"
							sx={{
								width: {xs: '100%', md: '240px'},
								flexShrink: 0,
								backgroundColor: '#ffffff',
								borderRadius: 5,
								'& .MuiOutlinedInput-root': {
									borderRadius: 5,
									'& fieldset': {border: 'none'},
								},
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon color="primary"/>
									</InputAdornment>
								),
							}}
						/>
					</Box>
				</Grid>
				{visibleDrivers.map((driver) => (
					<Grid item key={driver.id} xs={12} sm={6} md={4}>
						<DriverCard driver={driver}/>
					</Grid>
				))}
				{visibleDrivers.length === 0 &&
					<Grid item xs={12}>
						<Typography align="center" sx={{paddingTop: 4}}>
							{drivers.length === 0
								? 'Loading drivers…'
								: `No drivers match “${search}”.`}
						</Typography>
					</Grid>
				}
				{visibleDrivers.length > 0 &&
					<Grid item xs={12}>
						<Typography variant="caption" component="div" align="center" color="text.secondary">
							{visibleDrivers.length} of {drivers.length} drivers
							{platform !== 'all' ? ` — ${platform.toUpperCase()}` : ''}
						</Typography>
					</Grid>
				}
			</Grid>
		</Container>
	);
};

const mapStateToProps = (state) => {
	return {
		drivers: state.drivers.driverList,
		categories: state.drivers.driverCategories,
	};
};

export default connect(mapStateToProps, {getDrivers})(PortfolioBrowser);
